import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from '@/contexts/AuthContext';
import {
  listarNotificacoes, contarNaoLidas, marcarLidas, marcarTodasLidas,
} from '@/services/notificacoes.service';

export function useNotificacoes() {
  const { user } = useAuthContext();
  const userId = user?.id ?? null;
  const queryClient = useQueryClient();

  const lista = useQuery({
    queryKey: ['notificacoes', userId],
    queryFn: () => listarNotificacoes(userId as string),
    enabled: !!userId,
  });

  const naoLidas = useQuery({
    queryKey: ['notificacoes-nao-lidas', userId],
    queryFn: () => contarNaoLidas(userId as string),
    enabled: !!userId,
  });

  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`notificacoes-${userId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notificacoes', filter: `user_id=eq.${userId}` },
        () => {
          queryClient.invalidateQueries({ queryKey: ['notificacoes', userId] });
          queryClient.invalidateQueries({ queryKey: ['notificacoes-nao-lidas', userId] });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, queryClient]);

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ['notificacoes', userId] });
    queryClient.invalidateQueries({ queryKey: ['notificacoes-nao-lidas', userId] });
  };

  const marcarLidasMut = useMutation({ mutationFn: marcarLidas, onSuccess: invalidar });
  const marcarTodasMut = useMutation({ mutationFn: marcarTodasLidas, onSuccess: invalidar });

  return {
    notificacoes: lista.data ?? [],
    naoLidas: naoLidas.data ?? 0,
    isLoading: lista.isLoading,
    marcarLidas: (ids: string[]) => marcarLidasMut.mutate(ids),
    marcarTodas: () => marcarTodasMut.mutate(),
  };
}
